import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import Layout from "../components/Layout";
import BlogCard from "../components/Blog/BlogCard";
import LoadingSpinner from "../components/LoadingSpinner";
import images from "../common/images";
import { useFetchBlogs, useFetchBlogsHome } from "../hooks/useFetchBlogs";
import "./blog.css";

const POSTS_PER_PAGE = 12;

const Blog = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get("category") || "all";
  const searchParam = searchParams.get("q") || "";
  const sortBy = searchParams.get("sort_by") || "";
  const sortDir = searchParams.get("sort_dir") || "";

  const [searchInput, setSearchInput] = useState(searchParam);

  const {
    data: homeData,
    isLoading: homeLoading,
  } = useFetchBlogsHome();

  const categories = homeData?.categories || [{ slug: "all", name: "All" }];
  const hero = homeData?.hero;

  const selectedCategory = useMemo(() => {
    if (!categoryParam || categoryParam === "all") return null;
    return (
      categories.find(
        (cat) => cat.slug === categoryParam || cat.id === categoryParam
      ) || null
    );
  }, [categories, categoryParam]);

  const categoryId =
    selectedCategory && selectedCategory.slug !== "all"
      ? selectedCategory.id
      : undefined;

  const {
    data: blogsData,
    fetchNextPage,
    hasNextPage,
    isLoading: blogsLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useFetchBlogs({
    q: searchParam,
    limit: POSTS_PER_PAGE,
    category: categoryId,
    sort_by: sortBy || undefined,
    sort_dir: sortDir || undefined,
    enabled: !homeLoading,
  });

  const posts =
    blogsData?.pages?.flatMap((page) => page?.items || []).filter(Boolean) ||
    [];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [categoryParam, searchParam]);

  useEffect(() => {
    setSearchInput(searchParam);
  }, [searchParam]);

  const updateParams = (updates) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (
        value === undefined ||
        value === null ||
        value === "" ||
        (key === "category" && value === "all")
      ) {
        next.delete(key);
      } else {
        next.set(key, String(value));
      }
    });
    next.delete("page");
    setSearchParams(next);
  };

  const handleCategorySelect = (slug) => {
    updateParams({ category: slug });
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    updateParams({ q: searchInput.trim() });
  };

  const showLoader =
    homeLoading || ((blogsLoading || isFetching) && !posts.length);

  return (
    <Layout>
      <div className="azzir-blog">
        <section
          className="azzir-blog-hero"
          style={{
            backgroundImage: `url(${
              import.meta.env.VITE_HOME_HERO_IMG_URL || images.hero
            })`,
          }}
        >
          <div className="container">
            <div className="azzir-blog-hero__content">
              <span className="azzir-blog-hero__label">
                {hero?.badge || "Blog"}
              </span>
              <h1 className="azzir-blog-hero__title">
                {hero?.title || "Discover. Read. Get Inspired."}
              </h1>
              <p className="azzir-blog-hero__subtitle">
                {hero?.subtitle ||
                  "Nightlife guides, club picks, events and stories to help you plan better nights out with Azzir."}
              </p>
            </div>
          </div>
        </section>

        <section className="azzir-blog-main">
          <div className="container">
            <div className="azzir-blog-toolbar">
              <div className="azzir-blog-filters" role="tablist">
                {categories.map((cat) => (
                  <button
                    key={cat.id || cat.slug}
                    type="button"
                    role="tab"
                    aria-selected={categoryParam === cat.slug}
                    className={`azzir-blog-filter${
                      categoryParam === cat.slug ? " is-active" : ""
                    }`}
                    onClick={() => handleCategorySelect(cat.slug)}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>

              <form
                className="azzir-blog-search"
                onSubmit={handleSearchSubmit}
                role="search"
              >
                <label htmlFor="blog-search" className="sr-only">
                  Search blog posts
                </label>
                <i className="fas fa-search" aria-hidden="true" />
                <input
                  id="blog-search"
                  type="search"
                  placeholder="Search posts..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                />
                <button type="submit">Search</button>
              </form>
            </div>

            <div className="azzir-blog-layout azzir-blog-layout--full">
              <div className="azzir-blog-layout__content">
                {showLoader ? (
                  <LoadingSpinner />
                ) : isError ? (
                  <div className="azzir-blog-empty">
                    <h3>Couldn’t load posts</h3>
                    <p>{error?.message || "Please try again in a moment."}</p>
                  </div>
                ) : posts.length ? (
                  <InfiniteScroll
                    dataLength={posts.length}
                    next={hasNextPage ? fetchNextPage : refetch}
                    hasMore={Boolean(hasNextPage)}
                    loader={
                      <LoadingSpinner className="spinner-container--inline" />
                    }
                    refreshFunction={refetch}
                  >
                    <div className="azzir-blog-grid azzir-blog-grid--4">
                      {posts.map((post) => (
                        <BlogCard key={post.id} post={post} />
                      ))}
                    </div>
                  </InfiniteScroll>
                ) : (
                  <div className="azzir-blog-empty">
                    <h3>No posts found</h3>
                    <p>
                      Try another category or clear your search to see more
                      stories.
                    </p>
                    <button
                      type="button"
                      className="azzir-blog-btn"
                      onClick={() => {
                        setSearchInput("");
                        setSearchParams({});
                      }}
                    >
                      Clear filters
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>

        <section className="azzir-blog-cta">
          <div className="container">
            <div className="azzir-blog-cta__inner">
              <h2>Ready for Your Next Night Out?</h2>
              <p>
                Discover clubs, events and experiences happening near you.
              </p>
              <Link to="/events" className="azzir-blog-btn">
                Explore Events <i className="fas fa-arrow-right" />
              </Link>
            </div>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default Blog;
